import { useState, useEffect, useCallback, useRef } from 'react';
import type { Match, LiveMatchState, Player, BallColour, FoulType, FrameResult } from '@shootout-comps/types';
import {
  BALL_VALUES,
  SHOOTOUT_MATCH_DURATION_SECONDS,
  SHOOTOUT_HALF_TIME_SECONDS,
  SHOOTOUT_SHOT_TIME_SECONDS_FIRST_HALF,
  SHOOTOUT_SHOT_TIME_SECONDS_SECOND_HALF,
} from '@shootout-comps/types';
import type { AudioAdapter } from './AudioAdapter';
import type { ConnectionStatus } from '../socket/createSocket';
import { getLegalBalls, computeBreak, computeHighBreak, computeAvgShotTime } from './scoring';

export type FoulMode = 'off' | 'selecting';
export type FlashState = 'none' | 'shot-expired' | 'match-expired';

// A structural subset of @shootout-comps/i18n's Translations — kept local (rather than
// importing the full type) so libs/core doesn't depend on libs/i18n, which itself depends
// on libs/core for KeyValueStorage. Any full Translations object satisfies this shape.
export interface MatchPlayStrings {
  voiceHalfTime: string;
  noPotnPlayerChanged: string;
  lastShotUndone: string;
  cannotUndo: string;
  scoresInvalid: string;
}

export interface UseMatchPlayOptions {
  matchId: string;
  apiBaseUrl: string;
  getAccessToken: () => Promise<string | null>;
  audio: AudioAdapter;
  isStandardTournament: boolean;
  t: MatchPlayStrings;
  // Both platforms' socket singletons expose the same shape (built via
  // libs/core/src/socket/createSocket.ts) — the caller supplies its own instance.
  useMatchSocket: (matchId: string | null, handlers: Record<string, (data: unknown) => void>) => void;
  useConnectionStatus: () => ConnectionStatus;
  // Server assigns 'scorer' to the first socket to join a match's room and 'spectator' to
  // every later joiner (apps/api/src/index.ts's match:join handler). Routed through the
  // hook's own single match:join subscription rather than a second useMatchSocket call —
  // a second join from the same socket would itself count as "later" and self-trigger
  // spectator mode. Web-only (no spectator UI on mobile); undefined is a no-op.
  onRoleAssigned?: (role: 'scorer' | 'spectator') => void;
}

// The shared core of MatchPlayPage.tsx's state machine — everything except JSX. Covers the
// Phase 1 core loop: start → break-off → pot/miss/foul → undo → half-time → end. Deliberately
// leaves out web-only/admin extras (spectator mode, widescreen, tournament-wide pause-all,
// superuser debug-jump, restart-match, keyboard shortcuts) — those stay platform-local.
export function useMatchPlay(opts: UseMatchPlayOptions) {
  const { matchId, apiBaseUrl, getAccessToken, audio, isStandardTournament, t, useMatchSocket, useConnectionStatus, onRoleAssigned } = opts;
  const { silenceAll, unsilence } = audio;
  const API = `${apiBaseUrl}/api`;

  const [match, setMatch]             = useState<Match | null>(null);
  const [elapsed, setElapsed]         = useState(0);
  const [shotElapsed, setShotElapsed] = useState(0);
  const shotElapsedRef = useRef(0);
  const elapsedRef      = useRef(0);
  const [foulMode, setFoulMode]       = useState<FoulMode>('off');
  const [submitting, setSubmitting]   = useState(false);
  const [showEnd, setShowEnd]         = useState(false);
  const [finishScore1, setFinishScore1] = useState('');
  const [finishScore2, setFinishScore2] = useState('');
  // High break entered/confirmed on the finish dialog — auto-filled from the tracked shots (if
  // any) when the dialog opens, always editable, and submitted alongside the result either way
  // (a match finished without any ball-by-ball scoring has no computed value to fall back on).
  const [finishHighBreak1, setFinishHighBreak1] = useState('');
  const [finishHighBreak2, setFinishHighBreak2] = useState('');
  const [toast, setToast]             = useState<string | null>(null);
  const [undoing, setUndoing]         = useState(false);
  const [flash, setFlash]             = useState<FlashState>('none');
  const [clockPaused, setClockPaused] = useState(false);
  const [foulPending, setFoulPending] = useState<{ penalty: number; playerName: string } | null>(null);
  const foulPendingRef = useRef(false);
  const submittingRef  = useRef(false);
  const [foulValuePicking, setFoulValuePicking] = useState(false);
  const [shotClockPaused, setShotClockPaused] = useState(false);
  const shotClockPausedRef = useRef(false);
  const [restarting, setRestarting] = useState(false);
  const isStandardRef = useRef(isStandardTournament);
  isStandardRef.current = isStandardTournament;

  const shotTimerRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentShotLimitRef  = useRef<number>(SHOOTOUT_SHOT_TIME_SECONDS_FIRST_HALF);
  const shotStartRef         = useRef<Date | null>(null);
  const shotPausedAtRef      = useRef<number>(0);
  const clockPausedRef       = useRef(false);
  const frozenElapsedRef     = useRef<number | null>(null);
  const prevShotLeftRef      = useRef(999);
  const prevRemainingRef     = useRef(9999);
  const halfAnnouncedRef     = useRef(false);
  const shotBuzzedRef        = useRef(false);
  const matchBuzzedRef       = useRef(false);
  const inProgressRef        = useRef(false);
  const matchDurationRef     = useRef<number>(SHOOTOUT_MATCH_DURATION_SECONDS);
  const halfTimeRef          = useRef<number>(SHOOTOUT_HALF_TIME_SECONDS);
  const shotTimeFirstHalfRef = useRef<number>(SHOOTOUT_SHOT_TIME_SECONDS_FIRST_HALF);
  const shotTimeSecondHalfRef= useRef<number>(SHOOTOUT_SHOT_TIME_SECONDS_SECOND_HALF);
  const isMountedRef         = useRef(true);

  useEffect(() => { shotElapsedRef.current = shotElapsed; }, [shotElapsed]);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const applyTimingFromLiveState = useCallback((ls: LiveMatchState) => {
    if (ls.matchDurationSeconds)          matchDurationRef.current      = ls.matchDurationSeconds;
    if (ls.halfTimeSeconds !== undefined) halfTimeRef.current           = ls.halfTimeSeconds;
    if (ls.shotTimeFirstHalf)             shotTimeFirstHalfRef.current  = ls.shotTimeFirstHalf;
    if (ls.shotTimeSecondHalf)            shotTimeSecondHalfRef.current = ls.shotTimeSecondHalf;
  }, []);

  const resetShotClock = useCallback(() => {
    if (foulPendingRef.current) return;
    if (isStandardRef.current) return;
    if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    shotStartRef.current    = new Date();
    shotPausedAtRef.current = 0;
    setShotElapsed(0);
    prevShotLeftRef.current = 999;
    shotBuzzedRef.current   = false;
    clockPausedRef.current  = false;
    setClockPaused(false);
    // Clear any manual shot clock pause — new shot always starts running
    shotClockPausedRef.current = false;
    setShotClockPaused(false);
    currentShotLimitRef.current = (halfTimeRef.current > 0 && elapsedRef.current >= halfTimeRef.current)
      ? shotTimeSecondHalfRef.current
      : shotTimeFirstHalfRef.current;
    shotTimerRef.current = setInterval(() => {
      if (shotStartRef.current && !clockPausedRef.current) {
        setShotElapsed(Math.floor((Date.now() - shotStartRef.current.getTime()) / 1000));
      }
    }, 250);
  }, []);

  const apiCall = useCallback(async (path: string, body?: object): Promise<Match> => {
    const token = await getAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res  = await fetch(`${API}${path}`, { method: 'POST', headers, body: JSON.stringify(body ?? {}) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? 'Request failed');
    return data as Match;
  }, [API, getAccessToken]);

  const clearFlash = useCallback(() => {
    setFlash('none');
    shotBuzzedRef.current  = false;
    matchBuzzedRef.current = false;
  }, []);

  // ── Fetch on mount (and on reconnect — see below) ───────────────────────────
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);

  const loadMatch = useCallback(async () => {
    const token = await getAccessToken();
    const res  = await fetch(`${API}/matches/${matchId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const data = await res.json() as Match;
    setMatch(data);
    inProgressRef.current = data.liveState?.phase === 'in_progress';
    if (data.liveState) applyTimingFromLiveState(data.liveState as LiveMatchState);
    if (data.liveState?.elapsedSeconds) {
      setElapsed(data.liveState.elapsedSeconds);
      if (halfTimeRef.current > 0 && data.liveState.elapsedSeconds >= halfTimeRef.current) halfAnnouncedRef.current = true;
    }
    const ls = data.liveState as (LiveMatchState & { breakOffPending?: boolean }) | undefined;
    const isInProgress   = ls?.phase === 'in_progress';
    const isPaused       = ls?.clockPaused === true;
    const awaitingBreak  = ls?.breakOffPending === true;
    if (isInProgress && isPaused && !awaitingBreak) {
      // Match was paused when the client navigated away — resume it automatically
      // rather than showing a confirmation dialog (kept out of Phase 1 scope).
      apiCall(`/matches/${matchId}/resume-clock`).catch(() => null);
      clockPausedRef.current = false;
      setClockPaused(false);
      inProgressRef.current = true;
      resetShotClock();
    } else if (isInProgress && !awaitingBreak) {
      resetShotClock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, resetShotClock, applyTimingFromLiveState, getAccessToken, API]);

  useEffect(() => {
    loadMatch();
    return () => {
      if (shotTimerRef.current) clearInterval(shotTimerRef.current);
      if (!isMountedRef.current && inProgressRef.current && !isStandardRef.current) {
        getAccessToken().then(token => {
          fetch(`${API}/matches/${matchId}/pause-clock`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          }).catch(() => null);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, loadMatch]);

  // ── Reconnect resync — a separate effect (deliberately not folded into the mount effect
  // above) so a reconnect blip never triggers that effect's unmount-only pause-clock cleanup.
  // Any match:* events missed while disconnected are recovered by this plain refetch, since
  // every socket payload here is the full Match object — nothing to diff, just catch up.
  const { reconnectCount } = useConnectionStatus();
  useEffect(() => {
    if (reconnectCount > 0) loadMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectCount, loadMatch]);

  // ── Sockets ───────────────────────────────────────────────────────────────
  useMatchSocket(matchId, {
    'match:role': (d: unknown) => {
      const { role } = d as { matchId: string; role: 'scorer' | 'spectator' };
      onRoleAssigned?.(role);
    },
    'match:tick': (d: unknown) => {
      if (clockPausedRef.current) return;
      setElapsed((d as { elapsedSeconds: number }).elapsedSeconds);
    },
    'match:shot': (d: unknown) => {
      const shotMatch = d as Match;
      setMatch(shotMatch);
      inProgressRef.current = shotMatch.liveState?.phase === 'in_progress';
      if (!foulPendingRef.current) resetShotClock();
    },
    'match:started': (d: unknown) => {
      const m = d as Match;
      setMatch(m); inProgressRef.current = true; halfAnnouncedRef.current = false;
      if (m.liveState) applyTimingFromLiveState(m.liveState as LiveMatchState);
    },
    'match:break-off': (d: unknown) => {
      const m = d as Match;
      setMatch(m); inProgressRef.current = true;
      if (m.liveState) applyTimingFromLiveState(m.liveState as LiveMatchState);
    },
    'match:ended': (d: unknown) => {
      setMatch(d as Match); inProgressRef.current = false;
      if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    },
    // A frame just ended but the match (best-of-N) hasn't been decided yet — liveState has
    // already been reset server-side for the next frame (fresh clock/handicap/reds), same
    // shape as a brand-new match start. Mirror handleStart's reset of the audio-trigger refs.
    'match:frame-complete': (d: unknown) => {
      const m = d as Match;
      setMatch(m);
      inProgressRef.current = true;
      halfAnnouncedRef.current = false;
      matchBuzzedRef.current   = false;
      shotBuzzedRef.current    = false;
      prevRemainingRef.current = 9999;
      prevShotLeftRef.current  = 999;
      frozenElapsedRef.current = null;
      clockPausedRef.current   = false;
      setClockPaused(false);
      setElapsed(0);
      if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
      if (m.liveState) applyTimingFromLiveState(m.liveState as LiveMatchState);
      unsilence();
      const lastFrame = m.frames?.[m.frames.length - 1];
      if (lastFrame) showToast(`🔴 Frame ${lastFrame.frameNumber} complete — ${m.frameWins1 ?? 0}-${m.frameWins2 ?? 0}`);
    },
    'match:clock-paused': (d: unknown) => {
      const incoming = d as Match;
      setMatch(prev => {
        if (!prev) return incoming;
        const incomingShots = incoming.liveState?.shots ?? [];
        const localShots    = prev.liveState?.shots ?? [];
        const shots = incomingShots.length >= localShots.length ? incomingShots : localShots;
        return { ...incoming, liveState: incoming.liveState ? { ...incoming.liveState, shots } : prev.liveState };
      });
      clockPausedRef.current = true;
      setClockPaused(true);
      if (frozenElapsedRef.current === null) frozenElapsedRef.current = elapsed;
      shotPausedAtRef.current = shotElapsedRef.current;
      if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
      silenceAll();
    },
    'match:free-ball': (d: unknown) => {
      const incoming = d as Match;
      setMatch(prev => {
        if (!prev) return incoming;
        const incomingShots = incoming.liveState?.shots ?? [];
        const localShots    = prev.liveState?.shots ?? [];
        const shots = incomingShots.length >= localShots.length ? incomingShots : localShots;
        return { ...incoming, liveState: incoming.liveState ? { ...incoming.liveState, shots } : prev.liveState };
      });
    },
    'match:respot-black': (d: unknown) => {
      const incoming = d as Match;
      setMatch(prev => {
        if (!prev) return incoming;
        const incomingShots = incoming.liveState?.shots ?? [];
        const localShots    = prev.liveState?.shots ?? [];
        const shots = incomingShots.length >= localShots.length ? incomingShots : localShots;
        return { ...incoming, liveState: incoming.liveState ? { ...incoming.liveState, shots } : prev.liveState };
      });
    },
    'match:clock-resumed': (d: unknown) => {
      const incoming = d as Match;
      setMatch(prev => {
        if (!prev) return incoming;
        const incomingShots = incoming.liveState?.shots ?? [];
        const localShots    = prev.liveState?.shots ?? [];
        const shots = incomingShots.length >= localShots.length ? incomingShots : localShots;
        const incomingBreakOffPending = (incoming.liveState as unknown as { breakOffPending?: boolean })?.breakOffPending;
        const localBreakOffPending    = (prev.liveState as unknown as { breakOffPending?: boolean })?.breakOffPending;
        const breakOffPending = (localBreakOffPending === false) ? false : (incomingBreakOffPending ?? false);
        return { ...incoming, liveState: incoming.liveState ? { ...incoming.liveState, shots, breakOffPending } : prev.liveState };
      });
      frozenElapsedRef.current = null;
      clockPausedRef.current = false;
      setClockPaused(false);
      inProgressRef.current = true;
      foulPendingRef.current = false;
      setFoulPending(null);
      unsilence();
      resetShotClock();
    },
  });

  // ── Shot clock audio ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!inProgressRef.current) return;
    if (isStandardRef.current) return;
    const shotLimit = currentShotLimitRef.current;
    const timeLeft  = Math.max(0, shotLimit - shotElapsed);
    const prev      = prevShotLeftRef.current;

    if (prev > 5 && timeLeft <= 5 && timeLeft > 0) audio.playCountdownBeeps(timeLeft);

    if (timeLeft === 0 && prev > 0 && !shotBuzzedRef.current) {
      shotBuzzedRef.current = true;
      audio.playBuzzer();
      clockPausedRef.current = true;
      setClockPaused(true);
      if (frozenElapsedRef.current === null) frozenElapsedRef.current = elapsedRef.current;
      shotPausedAtRef.current = shotElapsedRef.current;
      if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
      silenceAll();
      foulPendingRef.current = true;
      apiCall(`/matches/${matchId}/pause-clock`).catch(() => null);
      apiCall(`/matches/${matchId}/shot`, { ballPotted: null, isFoul: true, foulType: 'foul_miss' })
        .then(u => {
          setMatch(u);
          const ls = u.liveState as LiveMatchState | undefined;
          const incomingPlayer = ls
            ? (ls.currentPlayerId === ls.player1Id ? u.player1 : u.player2) as Player
            : (u.player1 as Player);
          setFoulPending({ penalty: 4, playerName: incomingPlayer.name });
        })
        .catch(() => null);
    }
    prevShotLeftRef.current = timeLeft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotElapsed, elapsed, apiCall, matchId]);

  // ── Match clock audio ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!inProgressRef.current) return;
    if (isStandardRef.current) return;
    const remaining = matchDurationRef.current - elapsed;
    const prev      = prevRemainingRef.current;

    if (halfTimeRef.current > 0 && prev > halfTimeRef.current && remaining <= halfTimeRef.current && !halfAnnouncedRef.current) {
      halfAnnouncedRef.current = true;
      audio.speakAnnouncement(t.voiceHalfTime);
    }

    if (remaining <= 0 && prev > 0 && !matchBuzzedRef.current) {
      matchBuzzedRef.current = true;
      audio.playMatchExpiry();
      setFlash('match-expired');
    }
    prevRemainingRef.current = remaining;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed, t.voiceHalfTime]);

  const handleStart = useCallback(async () => {
    setSubmitting(true); halfAnnouncedRef.current = false; matchBuzzedRef.current = false; clearFlash();
    try {
      const u = await apiCall(`/matches/${matchId}/start`);
      if (u.liveState) applyTimingFromLiveState(u.liveState as LiveMatchState);
      setMatch(u);
    } finally { setSubmitting(false); }
  }, [apiCall, matchId, applyTimingFromLiveState, clearFlash]);

  const handleBreakOff = useCallback(async (breakingPlayerId?: string) => {
    setSubmitting(true); clearFlash();
    try {
      const u = await apiCall(`/matches/${matchId}/break-off`, breakingPlayerId ? { breakingPlayerId } : undefined);
      if (u.liveState) applyTimingFromLiveState(u.liveState as LiveMatchState);
      setMatch(u);
    } finally { setSubmitting(false); }
  }, [apiCall, matchId, applyTimingFromLiveState, clearFlash]);

  const handleShot = useCallback(async (ball: BallColour | null, isFoul = false, foulType?: FoulType) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    silenceAll();
    setSubmitting(true); setFoulMode('off'); setFoulValuePicking(false); clearFlash();
    if (isFoul) foulPendingRef.current = true;
    try {
      const u = await apiCall(`/matches/${matchId}/shot`, { ballPotted: ball, isFoul, foulType });
      if (isFoul) {
        clockPausedRef.current = true;
        setClockPaused(true);
        if (frozenElapsedRef.current === null) frozenElapsedRef.current = elapsedRef.current;
        shotPausedAtRef.current = shotElapsedRef.current;
        if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
        apiCall(`/matches/${matchId}/pause-clock`).catch(() => null);
        const penalty = Math.max(ball ? BALL_VALUES[ball as BallColour] ?? 0 : 0, 4);
        const ls = u.liveState as LiveMatchState | undefined;
        const incomingPlayer = ls
          ? (ls.currentPlayerId === ls.player1Id ? u.player1 : u.player2) as Player
          : (u.player1 as Player);
        setFoulPending({ penalty, playerName: incomingPlayer.name });
        setMatch(u);
      } else {
        unsilence();
        setMatch(u); resetShotClock();
        if (ball) showToast(`✓ ${ball} +${BALL_VALUES[ball]}`);
        else showToast(t.noPotnPlayerChanged);
      }
    } catch (e) { showToast(`❌ ${e instanceof Error ? e.message : 'Error'}`); }
    finally { submittingRef.current = false; setSubmitting(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, matchId, clearFlash, resetShotClock, showToast, t.noPotnPlayerChanged, silenceAll, unsilence]);

  // Pause/resume the shot clock only — the match clock keeps running. Web-only feature
  // (no button for it on mobile); kept here because it needs the same internal timer refs
  // as the rest of the clock machinery.
  const handlePauseShotClockOnly = useCallback(() => {
    shotClockPausedRef.current = true;
    setShotClockPaused(true);
    shotPausedAtRef.current = shotElapsedRef.current;
    if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
    silenceAll();
  }, [silenceAll]);

  const handleResumeShotClockOnly = useCallback(() => {
    shotClockPausedRef.current = false;
    setShotClockPaused(false);
    const pausedFor = shotPausedAtRef.current;
    shotStartRef.current = new Date(Date.now() - pausedFor * 1000);
    if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    shotTimerRef.current = setInterval(() => {
      if (shotStartRef.current && !clockPausedRef.current && !shotClockPausedRef.current) {
        setShotElapsed(Math.floor((Date.now() - shotStartRef.current.getTime()) / 1000));
      }
    }, 250);
    unsilence();
  }, [unsilence]);

  // Generic freeze/resume-from-pause primitives for callers that pause the clock via their
  // own API call (e.g. web's tournament-wide "pause all clocks" admin action) rather than
  // this match's own pause-clock/resume-clock endpoints. Resumes from where the shot clock
  // was paused rather than giving a fresh full shot clock (unlike resetShotClock).
  const freezeClock = useCallback(() => {
    clockPausedRef.current = true;
    setClockPaused(true);
    if (frozenElapsedRef.current === null) frozenElapsedRef.current = elapsedRef.current;
    shotPausedAtRef.current = shotElapsedRef.current;
    if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
    silenceAll();
  }, [silenceAll]);

  const unfreezeClock = useCallback(() => {
    frozenElapsedRef.current = null;
    clockPausedRef.current = false;
    setClockPaused(false);
    unsilence();
    shotStartRef.current = new Date(Date.now() - shotPausedAtRef.current * 1000);
    if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    shotTimerRef.current = setInterval(() => {
      if (shotStartRef.current && !clockPausedRef.current) {
        setShotElapsed(Math.floor((Date.now() - shotStartRef.current.getTime()) / 1000));
      }
    }, 250);
  }, [unsilence]);

  const pauseAndBack = useCallback((onBack: () => void) => {
    if (inProgressRef.current) {
      apiCall(`/matches/${matchId}/pause-clock`).catch(() => null);
      inProgressRef.current = false;
    }
    isMountedRef.current = false;
    onBack();
  }, [apiCall, matchId]);

  // `freeBall` — the referee is declaring the incoming player snookered by this foul, so their
  // very next shot may nominate any ball. Recorded before resuming so the offer is already live
  // the moment play does.
  const handleFoulResume = useCallback(async (freeBall = false) => {
    foulPendingRef.current = false; setFoulPending(null);
    if (freeBall) await apiCall(`/matches/${matchId}/free-ball`).catch(() => null);
    await apiCall(`/matches/${matchId}/resume-clock`).catch(() => null);
    frozenElapsedRef.current = null;
    clockPausedRef.current = false;
    setClockPaused(false);
    unsilence();
    resetShotClock();
  }, [apiCall, matchId, unsilence, resetShotClock]);

  // The referee records who won the coin toss/lag after the black is respotted on a level
  // frame — see the `blackTiedRespot` derived flag below and the API route's own comment for
  // why this can't be decided automatically.
  const handleRespotBlack = useCallback(async (playerId: string) => {
    try {
      const u = await apiCall(`/matches/${matchId}/respot-black`, { playerId });
      setMatch(u);
    } catch (e) { showToast(`❌ ${e instanceof Error ? e.message : 'Error'}`); }
  }, [apiCall, matchId, showToast]);

  const enterFoulMode = useCallback(() => {
    setFoulMode('selecting');
    clockPausedRef.current = true;
    setClockPaused(true);
    if (frozenElapsedRef.current === null) frozenElapsedRef.current = elapsedRef.current;
    shotPausedAtRef.current = shotElapsedRef.current;
    if (shotTimerRef.current) { clearInterval(shotTimerRef.current); shotTimerRef.current = null; }
    silenceAll();
    apiCall(`/matches/${matchId}/pause-clock`).catch(() => null);
  }, [apiCall, matchId, silenceAll]);

  const cancelFoulMode = useCallback(() => {
    setFoulMode('off');
    setFoulValuePicking(false);
    frozenElapsedRef.current = null;
    clockPausedRef.current = false;
    setClockPaused(false);
    unsilence();
    shotStartRef.current = new Date(Date.now() - shotPausedAtRef.current * 1000);
    if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    shotTimerRef.current = setInterval(() => {
      if (shotStartRef.current && !clockPausedRef.current) {
        setShotElapsed(Math.floor((Date.now() - shotStartRef.current.getTime()) / 1000));
      }
    }, 250);
    apiCall(`/matches/${matchId}/resume-clock`).catch(() => null);
  }, [apiCall, matchId, unsilence]);

  const handleUndo = useCallback(async () => {
    if (undoing) return;
    silenceAll();
    setUndoing(true); clearFlash(); foulPendingRef.current = false; setFoulPending(null);
    try {
      const u = await apiCall(`/matches/${matchId}/undo`);
      frozenElapsedRef.current = null;
      if (clockPausedRef.current) {
        clockPausedRef.current = false;
        setClockPaused(false);
        apiCall(`/matches/${matchId}/resume-clock`).catch(() => null);
      }
      unsilence(); setMatch(u); resetShotClock(); showToast(t.lastShotUndone);
    } catch (e) { showToast(`❌ ${e instanceof Error ? e.message : t.cannotUndo}`); }
    finally { setUndoing(false); }
  }, [undoing, silenceAll, clearFlash, apiCall, matchId, unsilence, resetShotClock, showToast, t.lastShotUndone, t.cannotUndo]);

  const handleEnd = useCallback(async (winnerId?: string, score1?: number, score2?: number) => {
    setSubmitting(true); clearFlash();
    const hb1 = parseInt(finishHighBreak1, 10);
    const hb2 = parseInt(finishHighBreak2, 10);
    try {
      const u = await apiCall(`/matches/${matchId}/end`, {
        winnerId, score1, score2,
        highBreak1: Number.isInteger(hb1) && hb1 >= 0 ? hb1 : undefined,
        highBreak2: Number.isInteger(hb2) && hb2 >= 0 ? hb2 : undefined,
      });
      setMatch(u); setShowEnd(false);
    } finally { setSubmitting(false); }
  }, [apiCall, matchId, clearFlash, finishHighBreak1, finishHighBreak2]);

  const handleFinishStandard = useCallback(async () => {
    if (!match) return;
    const s1 = parseInt(finishScore1, 10);
    const s2 = parseInt(finishScore2, 10);
    if (!Number.isInteger(s1) || !Number.isInteger(s2) || s1 < 0 || s2 < 0 || s1 === s2) {
      showToast(`❌ ${t.scoresInvalid}`); return;
    }
    const p1 = match.player1 as Player;
    const p2 = match.player2 as Player;
    const winnerId = s1 > s2 ? p1._id : p2._id;
    await handleEnd(winnerId, s1, s2);
  }, [match, finishScore1, finishScore2, showToast, t.scoresInvalid, handleEnd]);

  const openFinishDialog = useCallback(() => {
    if (isStandardTournament && match?.liveState) {
      setFinishScore1(String(match.liveState.score1 ?? 0));
      setFinishScore2(String(match.liveState.score2 ?? 0));
    }
    // Auto-fill from tracked shots regardless of format — if none were tracked (a match ended
    // without ever using the app's ball-by-ball scoring), this comes out 0 and stays freely
    // editable, same as an untouched field.
    if (match?.liveState) {
      const ls = match.liveState;
      setFinishHighBreak1(String(computeHighBreak(ls.shots, ls.player1Id)));
      setFinishHighBreak2(String(computeHighBreak(ls.shots, ls.player2Id)));
    } else {
      setFinishHighBreak1(''); setFinishHighBreak2('');
    }
    setShowEnd(true);
  }, [isStandardTournament, match]);

  // Web-only feature (no restart button on mobile); lives here because it needs to reset
  // the same internal audio-trigger/timing refs that handleStart resets.
  const handleRestart = useCallback(async () => {
    setRestarting(true);
    silenceAll();
    halfAnnouncedRef.current = false;
    matchBuzzedRef.current   = false;
    shotBuzzedRef.current    = false;
    prevRemainingRef.current = 9999;
    prevShotLeftRef.current  = 999;
    clearFlash();
    try {
      const u = await apiCall(`/matches/${matchId}/restart`);
      if (u.liveState) applyTimingFromLiveState(u.liveState as LiveMatchState);
      setMatch(u);
      setElapsed(0);
      resetShotClock();
      unsilence();
    } catch (e) {
      showToast(`❌ ${e instanceof Error ? e.message : 'Restart failed'}`);
    } finally {
      setRestarting(false);
    }
  }, [apiCall, matchId, silenceAll, clearFlash, applyTimingFromLiveState, resetShotClock, unsilence, showToast]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const ls              = match?.liveState as LiveMatchState | undefined;
  const p1               = match?.player1 as Player | undefined;
  const p2               = match?.player2 as Player | undefined;
  const notStarted       = !ls || ls.phase === 'not_started';
  const inProgress       = ls?.phase === 'in_progress';
  const breakOffPending  = !!inProgress && (ls as unknown as { breakOffPending?: boolean })?.breakOffPending === true;
  const complete         = ls?.phase === 'complete' || match?.status === 'complete';
  const winner           = (match?.winner as Player | null) ?? null;
  const curPlayer        = ls && p1 && p2 ? (ls.currentPlayerId === ls.player1Id ? p1 : p2) : null;
  const displayElapsed   = frozenElapsedRef.current !== null ? frozenElapsedRef.current : elapsed;
  const remaining        = matchDurationRef.current - displayElapsed;
  const inSecondHalf     = halfTimeRef.current > 0 && displayElapsed >= halfTimeRef.current;
  const shotLimit        = currentShotLimitRef.current;
  const shotLeft         = Math.max(0, shotLimit - shotElapsed);
  const shotWarn         = shotElapsed >= shotLimit - 5;
  const shotOver         = shotElapsed >= shotLimit;
  const freeBallAvailable = !!ls?.freeBallAvailable;
  const legalBalls       = ls ? getLegalBalls(ls.shots, ls.reds_remaining, freeBallAvailable) : new Set<BallColour>();
  // Strict (non-free-ball-widened) legality — the black-respot prompt only makes sense when
  // black is truly the sole legal ball, not when a free ball has also widened the options.
  const strictLegalBalls = ls ? getLegalBalls(ls.shots, ls.reds_remaining) : new Set<BallColour>();
  const blackTiedRespot  = !!ls && inProgress && ls.reds_remaining === 0
    && strictLegalBalls.size === 1 && strictLegalBalls.has('black') && ls.score1 === ls.score2;
  const p1Break          = ls && inProgress && p1 ? computeBreak(ls.shots, ls.player1Id) : 0;
  const p2Break          = ls && inProgress && p2 ? computeBreak(ls.shots, ls.player2Id) : 0;
  // Match high break — unlike the current break above, this isn't gated on `inProgress` so it
  // stays visible as a final stat once the match ends.
  const p1HighBreak      = ls && p1 ? computeHighBreak(ls.shots, ls.player1Id) : 0;
  const p2HighBreak      = ls && p2 ? computeHighBreak(ls.shots, ls.player2Id) : 0;
  const p1AvgShotTime    = ls && p1 ? computeAvgShotTime(ls.shots, ls.player1Id) : 0;
  const p2AvgShotTime    = ls && p2 ? computeAvgShotTime(ls.shots, ls.player2Id) : 0;
  const h1 = p1 ? ((p1 as unknown as { handicap?: number }).handicap ?? 0) : 0;
  const h2 = p2 ? ((p2 as unknown as { handicap?: number }).handicap ?? 0) : 0;
  const preMatchHandicapStart1 = h1 > h2 ? h1 - h2 : 0;
  const preMatchHandicapStart2 = h2 > h1 ? h2 - h1 : 0;
  const hasHandicap      = preMatchHandicapStart1 > 0 || preMatchHandicapStart2 > 0;
  const canUndo           = !!inProgress && (ls?.shots.length ?? 0) > 0;

  // ── Frames (best-of-N matches — standard/snooker900 only; absent/1 elsewhere) ──────────
  const bestOf            = match?.bestOf ?? 1;
  const isMultiFrame      = bestOf > 1;
  const framesToWin       = Math.ceil(bestOf / 2);
  const frameWins1        = match?.frameWins1 ?? 0;
  const frameWins2        = match?.frameWins2 ?? 0;
  const frames: FrameResult[] = match?.frames ?? [];
  const currentFrame      = ls?.currentFrame ?? 1;
  // Highest break across the WHOLE match (all completed frames plus the one in progress) —
  // distinct from p1HighBreak/p2HighBreak above, which are scoped to the current frame only
  // (ls.shots resets each frame). For a bestOf=1 match the two are identical.
  const p1MatchHighBreak  = Math.max(p1HighBreak, ...frames.map(f => f.highBreak1));
  const p2MatchHighBreak  = Math.max(p2HighBreak, ...frames.map(f => f.highBreak2));

  return {
    // raw state
    match, elapsed, shotElapsed, foulMode, submitting, showEnd,
    finishScore1, setFinishScore1, finishScore2, setFinishScore2,
    finishHighBreak1, setFinishHighBreak1, finishHighBreak2, setFinishHighBreak2,
    toast, undoing, flash, clockPaused, foulPending, foulValuePicking, setFoulValuePicking,
    shotClockPaused, restarting,

    // derived
    ls, p1, p2, notStarted, inProgress, breakOffPending, complete, winner, curPlayer,
    displayElapsed, remaining, inSecondHalf, shotLimit, shotLeft, shotWarn, shotOver,
    legalBalls, freeBallAvailable, blackTiedRespot, p1Break, p2Break, p1HighBreak, p2HighBreak, p1AvgShotTime, p2AvgShotTime,
    preMatchHandicapStart1, preMatchHandicapStart2, hasHandicap, canUndo,
    matchDuration: matchDurationRef.current, halfTime: halfTimeRef.current,
    bestOf, isMultiFrame, framesToWin, frameWins1, frameWins2, frames, currentFrame,
    p1MatchHighBreak, p2MatchHighBreak,

    // actions
    handleStart, handleBreakOff, handleShot, pauseAndBack, handleFoulResume, handleRespotBlack,
    handleUndo, handleEnd, handleFinishStandard, openFinishDialog,
    enterFoulMode, cancelFoulMode, setShowEnd,
    handlePauseShotClockOnly, handleResumeShotClockOnly, freezeClock, unfreezeClock,
    handleRestart,
  };
}

export type UseMatchPlayResult = ReturnType<typeof useMatchPlay>;
